import {
  AdminApprovalRequestDetailView,
  AdminApprovalRequestView,
} from '../../../../services/admin-approval-requests/admin-approval-requests.service';
import { AdminPaymentlineView } from '../../../../services/admin-payments/admin-payments.service';
import { AdminSessionAuditView } from '../../../../services/admin-sessions/admin-sessions.service';
import { ReportView } from '../../../../services/reports/reports.service';
import {
  BarRow,
  CancellationTotals,
  OrdersSummary,
  PaymentByMethod,
  SameDaysComparison,
  TotalsCard,
  TotalsMode,
  WaiterActionBreakdownRow,
} from '../types/dashboard-page.types';

export const asAmount = (value: number): string => new Intl.NumberFormat('he-IL', {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
}).format(value);

// Wrapped in LRE/PDF so "123.4 ₪" always renders number-then-symbol, even inside an RTL ancestor.
export const asCurrency = (value: number): string => `\u202A${asAmount(value)} ₪\u202C`;

export const addDays = (value: Date, days: number): Date => {
  const next = new Date(value);
  next.setHours(0, 0, 0, 0);
  next.setDate(next.getDate() + days);
  return next;
};

export const startOfIsraeliWeek = (value: Date): Date => addDays(value, -value.getDay());

const asDayMonth = (value: Date): string => `${value.getDate()}.${value.getMonth() + 1}`;

export const formatWeekLabel = (weekStart: Date): string => `${asDayMonth(weekStart)} – ${asDayMonth(addDays(weekStart, 6))}`;

export const formatHeaderDay = (value: Date): string => value.toLocaleDateString('he-IL', {
  weekday: 'long',
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

// Compact axis ticks: no decimals, thousands as K (5K ₪) so tick labels stay narrow.
export const asAxisCurrency = (value: number): string => {
  const compact = Math.abs(value) >= 1000
    ? `${new Intl.NumberFormat('he-IL', { maximumFractionDigits: 1 }).format(value / 1000)}K`
    : new Intl.NumberFormat('he-IL', { maximumFractionDigits: 0 }).format(value);
  return `\u202a${compact} ₪\u202c`;
};

export const asDate = (iso: string): string => new Date(iso).toLocaleDateString('he-IL');

export const asHour = (iso: string): string => new Date(iso).toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });

export const sum = (values: number[]): number => values.reduce((acc, value) => acc + value, 0);

const DAY_MS = 24 * 60 * 60 * 1000;

const calendarDay = (value: Date): number => Date.UTC(
  value.getFullYear(),
  value.getMonth(),
  value.getDate(),
) / DAY_MS;

const resolveCompleteComparison = (
  startDay: number,
  dayCount: number,
  closedSessionsByDay: Map<number, AdminSessionAuditView[]>,
  label: 'sameMonth' | 'sameWeek',
): SameDaysComparison => {
  const comparedSessions: AdminSessionAuditView[] = [];
  for (let offset = 0; offset < dayCount; offset += 1) {
    const daySessions = closedSessionsByDay.get(startDay + offset);
    if (!daySessions?.length) return null;
    comparedSessions.push(...daySessions);
  }
  return {
    label,
    baseline: sum(comparedSessions.map((session) => session.salesTotal)),
    sessionIds: comparedSessions.map((session) => session.sessionId),
  };
};

export const computeSameDaysComparison = (
  selectedSessions: AdminSessionAuditView[],
  allSessions: AdminSessionAuditView[],
  rangeStart?: Date | null,
  rangeEnd?: Date | null,
): SameDaysComparison => {
  if (selectedSessions.length === 0) return null;

  const selectedDays = selectedSessions.map((session) => calendarDay(new Date(session.openTime)));
  const sourceStart = rangeStart ? calendarDay(rangeStart) : Math.min(...selectedDays);
  const sourceEnd = rangeEnd ? calendarDay(rangeEnd) : Math.max(...selectedDays);
  const firstDay = Math.min(sourceStart, sourceEnd);
  const lastDay = Math.max(sourceStart, sourceEnd);
  const dayCount = lastDay - firstDay + 1;
  const sourceStartDate = new Date(firstDay * DAY_MS);
  const previousMonthLastDay = new Date(
    sourceStartDate.getUTCFullYear(),
    sourceStartDate.getUTCMonth(),
    0,
  ).getDate();
  const previousMonthTarget = new Date(Date.UTC(
    sourceStartDate.getUTCFullYear(),
    sourceStartDate.getUTCMonth() - 1,
    Math.min(sourceStartDate.getUTCDate(), previousMonthLastDay),
  ));
  const weekdayOffset = ((sourceStartDate.getUTCDay() - previousMonthTarget.getUTCDay() + 10) % 7) - 3;
  const previousMonthStart = calendarDay(previousMonthTarget) + weekdayOffset;
  const closedSessionsByDay = new Map<number, AdminSessionAuditView[]>();
  allSessions.filter((session) => session.state === 'closed').forEach((session) => {
    const day = calendarDay(new Date(session.openTime));
    closedSessionsByDay.set(day, [...(closedSessionsByDay.get(day) ?? []), session]);
  });

  return resolveCompleteComparison(previousMonthStart, dayCount, closedSessionsByDay, 'sameMonth')
    ?? resolveCompleteComparison(firstDay - 7, dayCount, closedSessionsByDay, 'sameWeek');
};

export const computeWaiterActionBreakdown = (
  rows: AdminApprovalRequestView[],
  persistedDiscountTotal?: number,
): WaiterActionBreakdownRow[] => {
  const byWaiter = new Map<string, Omit<WaiterActionBreakdownRow, 'name' | 'totalCost'>>();
  rows
    .filter((row) => row.status === 'approved')
    .forEach((row) => {
      const entry = byWaiter.get(row.requestedByName) ?? {
        onTheHouse: { count: 0, cost: 0 },
        cancel: { count: 0, cost: 0 },
        itemDiscount: { count: 0, cost: 0 },
        globalDiscount: { count: 0, cost: 0 },
      };
      if (row.actionType === 'on_the_house') {
        entry.onTheHouse.count += 1;
        entry.onTheHouse.cost += row.cost;
      } else if (row.actionType === 'cancel') {
        entry.cancel.count += 1;
        entry.cancel.cost += row.cost;
      } else if (row.actionType === 'item_discount') {
        entry.itemDiscount.count += 1;
        entry.itemDiscount.cost += row.cost;
      } else if (row.actionType === 'global_discount') {
        entry.globalDiscount.count += 1;
        entry.globalDiscount.cost += row.cost;
      }
      byWaiter.set(row.requestedByName, entry);
    });
  const breakdownRows = [...byWaiter.entries()]
    .map(([name, breakdown]) => ({
      name,
      ...breakdown,
      totalCost: breakdown.onTheHouse.cost + breakdown.cancel.cost + breakdown.itemDiscount.cost + breakdown.globalDiscount.cost,
    }));

  if (persistedDiscountTotal !== undefined) {
    const discountBuckets = breakdownRows.flatMap((row) => ([
      row.onTheHouse,
      row.itemDiscount,
      row.globalDiscount,
    ])).filter((bucket) => bucket.cost > 0);
    const auditDiscountTotal = sum(discountBuckets.map((bucket) => bucket.cost));
    let allocated = 0;
    discountBuckets.forEach((bucket, index) => {
      const cost = index === discountBuckets.length - 1
        ? persistedDiscountTotal - allocated
        : Math.round((bucket.cost / auditDiscountTotal) * persistedDiscountTotal * 10) / 10;
      bucket.cost = Math.max(0, cost);
      allocated += bucket.cost;
    });
    breakdownRows.forEach((row) => {
      row.totalCost = row.onTheHouse.cost + row.cancel.cost + row.itemDiscount.cost + row.globalDiscount.cost;
    });
  }

  return breakdownRows
    .sort((a, b) => b.totalCost - a.totalCost);
};

// `baseAmount` = tip-free sale; `amount` = tip-inclusive charge; `globalTipCollected` = the tip.
export const sumDonePaymentsByMethod = (
  payments: AdminPaymentlineView[],
  method: AdminPaymentlineView['method'],
  field: 'amount' | 'baseAmount' | 'globalTipCollected',
): number => sum(
  payments
    .filter((row) => row.status === 'done' && row.canceledAt == null && row.method === method)
    .map((row) => {
      if (field === 'globalTipCollected') return row.globalTipCollected ?? 0;
      return field === 'baseAmount' ? row.baseAmount : row.amount;
    }),
);

export const computePaymentByMethod = (payments: AdminPaymentlineView[], report: ReportView | null): PaymentByMethod => {
  if (payments.length === 0 && report) {
    return {
      cash: report.paymentsCash,
      credit: report.paymentsCredit,
      tenbis: report.paymentsTenbis,
      tipsCash: report.tipsCash,
      tipsCredit: report.tipsCredit,
      tipsTenbis: report.tipsTenbis,
      houseAccount: report.paymentsHouseAccount,
      tipsHouseAccount: report.tipsHouseAccount,
    };
  }

  return {
    cash: sumDonePaymentsByMethod(payments, 'cash', 'baseAmount'),
    credit: sumDonePaymentsByMethod(payments, 'credit', 'baseAmount'),
    tenbis: sumDonePaymentsByMethod(payments, 'tenbis', 'baseAmount'),
    tipsCash: report?.tipsCash ?? 0,
    tipsCredit: report?.tipsCredit ?? 0,
    tipsTenbis: report?.tipsTenbis ?? 0,
    houseAccount: sumDonePaymentsByMethod(payments, 'house_account', 'baseAmount'),
    tipsHouseAccount: report?.tipsHouseAccount ?? 0,
  };
};

export const computeGraphRows = (payments: AdminPaymentlineView[]): BarRow[] => {
  const doneRows = payments
    .filter((row) => row.status === 'done' && row.baseAmount > 0)
    .sort((a, b) => new Date(a.updatedAt).getTime() - new Date(b.updatedAt).getTime());

  if (doneRows.length === 0) {
    return [];
  }

  const bucket = new Map<string, number>();
  doneRows.forEach((row) => {
    const d = new Date(row.updatedAt);
    const key = `${String(d.getHours()).padStart(2, '0')}:00`;
    bucket.set(key, (bucket.get(key) ?? 0) + row.baseAmount);
  });

  return [...bucket.entries()]
    .map(([label, value]) => ({ label, value }))
    .slice(-7);
};

export const computeClosedOrdersSummary = (payments: AdminPaymentlineView[], report: ReportView | null): OrdersSummary => {
  const closedDone = payments.filter((row) => row.orderStatus === 'closed' && row.status === 'done');
  const uniqueOrders = new Set(closedDone.map((row) => row.orderCode));
  const amountFromPayments = sum(closedDone.map((row) => row.baseAmount));
  return {
    count: uniqueOrders.size,
    amount: uniqueOrders.size > 0 ? amountFromPayments : (report?.closedRevenue ?? amountFromPayments),
  };
};

export const computeOpenOrdersSummary = (payments: AdminPaymentlineView[], report: ReportView | null): OrdersSummary => {
  if (report) {
    return { count: report.openOrdersCount, amount: report.openOrdersTotal };
  }
  const openDone = payments.filter((row) => row.orderStatus === 'open' && row.status === 'done');
  const uniqueOrders = new Set(openDone.map((row) => row.orderCode));
  return { count: uniqueOrders.size, amount: sum(openDone.map((row) => row.baseAmount)) };
};

type CancellationDetail = {
  payload:
    | { actionType: 'cancel'; items: ReadonlyArray<{ orderlineId: number; quantity: number }> }
    | { actionType: Exclude<AdminApprovalRequestDetailView['payload']['actionType'], 'cancel'> };
  orderlines: ReadonlyArray<{ orderlineId: number; productName: string | null; quantity: number; unitPrice: number }>;
};

type CanceledOrderline = {
  orderlineId: number;
  productName: string | null;
  quantity: number;
  unitPrice: number;
};

export const getCanceledOrderlines = (detail: CancellationDetail): CanceledOrderline[] => {
  if (detail.payload.actionType !== 'cancel') {
    return [];
  }

  const orderlineById = new Map(detail.orderlines.map((line) => [line.orderlineId, line]));
  return detail.payload.items.map((item) => {
    const orderline = orderlineById.get(item.orderlineId);
    return {
      orderlineId: item.orderlineId,
      productName: orderline?.productName ?? null,
      quantity: item.quantity,
      unitPrice: orderline?.unitPrice ?? 0,
    };
  });
};

export const computeCancellationTotals = (
  cancelDetails: CancellationDetail[],
  report: ReportView | null,
): CancellationTotals => {
  const fullAmount = sum(
    cancelDetails.flatMap((detail) => getCanceledOrderlines(detail).map((line) => line.unitPrice * line.quantity)),
  );
  const totalItems = sum(
    cancelDetails.flatMap((detail) => getCanceledOrderlines(detail).map((line) => line.quantity)),
  );
  return {
    itemsCount: report?.canceledItemsCount ?? totalItems,
    itemsAmount: report?.canceledItemsAmount ?? fullAmount,
    orderCount: report?.canceledOrdersCount ?? 0,
  };
};

export const computeTotalsCards = (
  isOpen: boolean,
  closedOrdersSummary: OrdersSummary,
  openOrdersSummary: OrdersSummary,
  closedShiftValue: number,
): TotalsCard[] => (isOpen
  ? [
    { key: 'ordersTotal' as TotalsMode, title: 'סה״כ', value: closedOrdersSummary.amount + openOrdersSummary.amount, count: closedOrdersSummary.count + openOrdersSummary.count },
    { key: 'closedOrders' as TotalsMode, title: 'הזמנות סגורות', value: closedOrdersSummary.amount, count: closedOrdersSummary.count },
    { key: 'openOrders' as TotalsMode, title: 'הזמנות פתוחות', value: openOrdersSummary.amount, count: openOrdersSummary.count },
  ]
  : [
    { key: 'closedShift' as TotalsMode, title: 'סה״כ', value: closedShiftValue },
  ]);

export const formatPct = (value: number | null): string => {
  if (value === null || !Number.isFinite(value)) {
    return 'n/a';
  }
  return `${value >= 0 ? '+' : ''}${value.toFixed(1)}%`;
};

export const normalizeTenantToken = (value: string): string => value.toLowerCase().replace(/[^a-z0-9]/g, '');

export const isSameTenant = (candidateTenantId: string, routeTenantId: string): boolean => {
  if (candidateTenantId === routeTenantId) {
    return true;
  }
  const candidate = normalizeTenantToken(candidateTenantId);
  const route = normalizeTenantToken(routeTenantId);
  return candidate.length > 0 && route.length > 0 && (candidate.includes(route) || route.includes(candidate));
};

// Mirrors backend calculateOrderDiscount (apps/backend/src/modules/receipt/receipt-totals.util.ts):
// discountMode 'percentage' resolves against the net items total (after per-line discounts), clamped to it.
export const computeGlobalDiscountAmount = (
  netItemsTotal: number,
  discountMode: 'amount' | 'percentage' | null,
  discountInput: number | null,
): number => {
  if (!(netItemsTotal > 0) || !discountMode || discountInput == null) {
    return 0;
  }
  const raw = discountMode === 'amount' ? discountInput : (netItemsTotal * discountInput) / 100;
  return Math.round(Math.min(raw, netItemsTotal) * 10) / 10;
};

export const doesTenantAliasMatch = (value: string, aliases: string[]): boolean => {
  const normalizedValue = normalizeTenantToken(value);
  if (!normalizedValue) {
    return false;
  }
  return aliases.some((alias) => alias.includes(normalizedValue) || normalizedValue.includes(alias));
};
